import React from 'react'
import styled from '@emotion/styled'
import { useSelector } from 'react-redux'
import { Navigate, matchPath, useLocation } from 'react-router'
import { Button } from '@mobi/component-library/Base/Buttons'
import { LoadingSpinner } from '@mobi/component-library/Feedback/LoadingSpinner'
import { measurements, spacing } from '@mobi/component-library/Foundations'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { InfoHeader } from '@mobi/component-library/Typography/InfoHeader'
import { isReactNativeApp } from '@mobi/web-native-comms/web'
import { AppRoutes } from '@core/AppRoutes'
import { RacingSportsTabs } from '@core/Areas/Account/Pages/Activity/Components/RacingSportsTabs'
import { selectIsBrowserSupported } from '@core/Areas/KambiClient/Store/selectors'
import { KAMBI_CONTAINER_BG_COLOR, KAMBI_HOME_HASH_ROUTE } from '@core/Areas/KambiClient/constants'
import { useNewSportsOfferingFeature } from '@core/Areas/KambiSports/Offering/hooks/useNewSportsOfferingFeature'
import { useToggleKambiBetSlipVisibility } from '@core/Areas/KambiSports/Offering/hooks/useToggleKambiBetSlipVisibility'
import { appHistory } from '@core/Utils/routing/history'
import {
  useKambiMaintenanceFeature,
  useRouteEnterRedirect,
  useShouldShowSpinner,
  useShowTransactionTabs,
  useToggleKambiClientVisibility,
} from './hooks'

/** Handle Kambi route enter/leave events and component display */
export const KambiRouteEffects: React.FC = () => {
  const maintenanceEndDate = useKambiMaintenanceFeature()

  const isNewSportsFeatureActive = useNewSportsOfferingFeature()
  const { pathname } = useLocation()

  useToggleKambiClientVisibility(!maintenanceEndDate)
  useToggleKambiBetSlipVisibility(!maintenanceEndDate)
  useRouteEnterRedirect()

  const shouldShowTransactionTabs = useShowTransactionTabs()
  const shouldShowSpinner = useShouldShowSpinner()
  const shouldShowBrowserUnsupported = !useSelector(selectIsBrowserSupported)

  if (isNewSportsFeatureActive && matchPath(KAMBI_HOME_HASH_ROUTE, pathname)) {
    return <Navigate to={AppRoutes.Sports} />
  }

  if (maintenanceEndDate) {
    return (
      <ContainerStyled>
        <InfoHeader
          icon='info'
          header='Sports is Unavailable Due to Maintenance'
          subHeader={
            <Flex flexDirection='column' gap={spacing.sm}>
              <span>
                We expect to be back up and running by{' '}
                {new Date(maintenanceEndDate).toLocaleString('en-AU', {
                  weekday: 'long',
                  hour: 'numeric',
                  minute: 'numeric',
                })}
              </span>
              <span>Our customer service centre can be contacted on 1300 36 36 88</span>
              <Button color='primary' onClick={() => appHistory.push(AppRoutes.Home)}>
                Return to Home Page
              </Button>
            </Flex>
          }
        />
      </ContainerStyled>
    )
  }

  if (shouldShowSpinner) {
    return (
      <ContainerStyled data-testid='kambi-client-loading'>
        <LoadingSpinner />
      </ContainerStyled>
    )
  }

  if (shouldShowBrowserUnsupported) {
    const browserOrDeviceText = isReactNativeApp ? 'Device' : 'Browser'
    return (
      <ContainerStyled data-testid='kambi-client-unsupported'>
        <InfoHeader
          icon='error'
          header={`Unsupported ${browserOrDeviceText}`}
          subHeader={
            <Flex flexDirection='column' gap={spacing.sm}>
              <span>Sports is unavailable on this {browserOrDeviceText.toLowerCase()}.</span>
              <span>
                Please try a newer{' '}
                <a href='https://help.tabtouch.com.au/knowledge-base/faqs-mobile/'>version</a>.
              </span>
              <Button color='primary' onClick={() => appHistory.push(AppRoutes.Home)}>
                Return to Home Page
              </Button>
            </Flex>
          }
        />
      </ContainerStyled>
    )
  }

  if (shouldShowTransactionTabs) {
    return <RacingSportsTabs activeTab='sports' />
  }

  return null
}

// ======
// Styles
// ======

const ContainerStyled = styled.div({
  boxSizing: 'border-box',
  position: 'absolute',
  zIndex: 0,
  width: '100%',
  minHeight: `calc(100vh - ${measurements.mobi.headerHeight})`,
  padding: spacing.sm,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: KAMBI_CONTAINER_BG_COLOR,
  fontSize: '4rem',
})
